import clsx from 'clsx';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IS_PHONE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { usePersonalizationPromoInformation } from 'reactApp/hooks/usePersonalizationPromoInformation';
import { usePersonalizationTopBanner } from 'reactApp/hooks/usePersonalizationTopBanner';
import { isCurrentDarkTheme } from 'reactApp/modules/theme/theme.selectors';
import { InformationAlertBanner, InformationBannerType } from 'reactApp/ui/InformationAlertBanner/InformationAlertBanner';
import { sendDwh } from 'reactApp/utils/ga';

import styles from './PersonalizationTopBanner.css';
import {
    isPersonalizationTopBannerShowAllowed,
    markPersonalizationTopBannerShown,
    usePersonalizationTopBannerImg,
} from './PersonalizationTopBanner.helpers';

const ANALYTICS_NAME = 'mnz_personalization_top_banner';
const SHORT_TITLE_BREAKPOINT = 1240;

const sendDwhEvent = (action: 'show' | 'click' | 'close', buttonName?: string) => {
    const dwhData: Record<string, string> = {
        name: ANALYTICS_NAME,
        platform: IS_PHONE_BROWSER ? 'touch' : 'web',
    };
    if (buttonName) {
        dwhData.button_name = buttonName;
    }
    sendDwh({
        eventCategory: 'banner',
        action,
        dwh_enabled: true,
        dwhData,
    });
};

export const PersonalizationTopBanner = memo(() => {
    const isDarkTheme = useSelector(isCurrentDarkTheme);
    const { currentSpace, promoSpace, handleBuy } = usePersonalizationPromoInformation();
    const isBannerAllowed = usePersonalizationTopBanner();

    const [isVisible, setIsVisible] = useState(true);
    const [allowedToShow] = useState(() => isPersonalizationTopBannerShowAllowed());
    const [isShortTitle, setIsShortTitle] = useState(() => window.innerWidth < SHORT_TITLE_BREAKPOINT);

    const image = usePersonalizationTopBannerImg(promoSpace?.space);

    const shouldRender = isVisible && allowedToShow && isBannerAllowed && currentSpace && promoSpace;

    useEffect(() => {
        if (shouldRender) {
            markPersonalizationTopBannerShown();
            sendDwhEvent('show');
        }
    }, [shouldRender]);

    useEffect(() => {
        const handleResize = () => setIsShortTitle(window.innerWidth < SHORT_TITLE_BREAKPOINT);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleBuyClick = useCallback(() => {
        sendDwhEvent('click', 'add_space');
        handleBuy();
    }, [handleBuy]);

    const handleClose = useCallback(() => {
        sendDwhEvent('close');
        setIsVisible(false);
    }, []);

    if (!shouldRender) {
        return null;
    }

    const currentText = `${currentSpace.space} ${currentSpace.units}`;
    const promoText = `${promoSpace.space} ${promoSpace.units}`;
    const subtitle = isShortTitle
        ? `К ${currentText} добавится ${promoText}`
        : `К ${currentText} добавится ${promoText} — хватит для фото из путешествий и видео с праздников`;
    const title = isShortTitle ? 'Возможно, вам пригодится больше места' : 'Возможно, вам пригодится больше места в Облаке';

    return (
        <div className={clsx(styles.wrapper, { [styles.darkRoot]: isDarkTheme })}>
            <img src={image} className={styles.image} alt="" />
            <InformationAlertBanner
                title={title}
                content={subtitle}
                primaryButtonTitle="Добавить за 1 ₽"
                type={InformationBannerType.info}
                hideIcon={true}
                primaryButtonClassName={styles.primaryButton}
                onPrimaryClick={handleBuyClick}
                closable
                onCancel={handleClose}
            />
        </div>
    );
});

PersonalizationTopBanner.displayName = 'PersonalizationTopBanner';
